import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Videocam,
  ChevronRight,
  FiberManualRecord,
  AccessTime,
  Check,
  WarningAmberRounded,
} from "@mui/icons-material";
import RecordRTC from "recordrtc";
import { AxiosService } from "../../../services/axios/axiosService";
import { useNavigate, useLocation } from "react-router-dom";
import Timer from "./Timer";
import InterviewFeedbackModal from "./InterviewFeedbackModal";
import {
  persistInterviewId,
  getPersistedId,
  clearPersistedId,
  saveProgress,
  loadProgress,
  clearProgress,
  loadSession,
  saveSession,
  clearSession,
  addLog,
  getLogs,
  clearLogs,
  clearAllForInterview,
} from "./interviewLogs";

// ============================Attempt Tracker=====================================================
// Tracks per-question recording attempts in localStorage.
//
// Attempt 1   → normal, no warning
// Attempt 2   → show FIRST warning popup ("last attempt, next refresh will auto-advance")
// Attempt 3   → show SECOND warning/alert THEN auto-advance + upload empty blob
// Attempt 4+  → auto-advance silently (safety net, should not normally be reached)
//
// Key:   "interview_attempts_<interviewId>"
// Value: { "1": 1, "2": 2, ... }  questionNum → attemptCount

const MAX_ATTEMPTS_PER_QUESTION = 3; // on 3rd attempt → show alert THEN auto-advance
const WARN_AT_ATTEMPT           = 2; // on 2nd attempt → show first warning popup
const ATTEMPTS_PREFIX           = "interview_attempts_";

const loadAttempts = (interviewId) => {
  try {
    const raw = localStorage.getItem(`${ATTEMPTS_PREFIX}${interviewId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveAttempts = (interviewId, attempts) => {
  try { localStorage.setItem(`${ATTEMPTS_PREFIX}${interviewId}`, JSON.stringify(attempts)); }
  catch (e) {  }
};

const clearAttempts = (interviewId) => {
  try { localStorage.removeItem(`${ATTEMPTS_PREFIX}${interviewId}`); } catch (e) {}
};

// Returns the NEW count after incrementing
const incrementAttempt = (interviewId, questionNum) => {
  const attempts = loadAttempts(interviewId);
  const newCount = (attempts[questionNum] ?? 0) + 1;
  attempts[questionNum] = newCount;
  saveAttempts(interviewId, attempts);
  return newCount;
};

// ============================ProgressTracker=================================================
const ProgressTracker = React.memo(({ currentQuestion, totalQuestions }) => (
  <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid rgba(148, 163, 184, 0.4)" }}>
    <Typography variant="body1" fontWeight={600} mb={2}>Questions:</Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5 }}>
      {Array.from({ length: totalQuestions }, (_, i) => {
        const num         = i + 1;
        const isActive    = num === currentQuestion;
        const isCompleted = num < currentQuestion;

        const borderColor   = isCompleted ? "#22c55e" : isActive ? "#3b82f6" : "#d4d7db";
        const textColor     = isCompleted ? "#166534" : isActive ? "#1d4ed8" : "#4b5563";
        const IconComponent = isCompleted ? Check : null;

        return (
          <Box
            key={num}
            sx={{
              position: "relative",
              width: 45, height: 45,
              borderRadius: 6,
              border: `2px solid ${borderColor}`,
              backgroundColor: "#ffffff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15, fontWeight: 600,
              color: textColor,
              boxSizing: "border-box",
              ...(isActive && { animation: "questionPulse 1.6s ease-in-out infinite" }),
              "@keyframes questionPulse": {
                "0%":   { boxShadow: "0 0 0 0 rgba(59,130,246,0.4)", transform: "translateY(0)" },
                "50%":  { boxShadow: "0 0 0 6px rgba(59,130,246,0)", transform: "translateY(-1px)" },
                "100%": { boxShadow: "0 0 0 0 rgba(59,130,246,0)",   transform: "translateY(0)" },
              },
            }}
          >
            {IconComponent && (
              <Box sx={{
                position: "absolute", top: -6, right: -6,
                width: 16, height: 16, borderRadius: 5,
                border: `1px solid ${borderColor}`,
                backgroundColor: "#22c55e",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 0 0 2px ${borderColor}`,
              }}>
                <IconComponent sx={{ fontSize: 12, color: "#f9fafb" }} />
              </Box>
            )}
            {num}
          </Box>
        );
      })}
    </Box>
  </Paper>
));

// ============================CurrentQuestion=================================================
const CurrentQuestion = React.memo(
  ({ question, questionNumber, onNext, isSubmit, isNextEnabled, isLoading }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid rgba(170, 168, 160, 0.3)",
        borderRadius: 2,
        height: "100%",
        display: "flex", flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
        <Typography variant="body1" fontWeight={600} color="text.secondary">
          Question - {questionNumber}
        </Typography>
      </Stack>

      <Typography variant="h6" fontWeight={400} mb={2}>
        {question}
      </Typography>

      <Button
        variant="contained"
        size="medium"
        endIcon={isLoading || isSubmit ? null : <ChevronRight />}
        onClick={onNext}
        disabled={!isNextEnabled || isLoading}
        sx={{ textTransform: "none", borderRadius: 1.5, mb: 2, mt: "auto", alignSelf: "flex-start", px: 2 }}
      >
        {isSubmit ? "Submit Interview" : "Next Question"}
      </Button>
    </Paper>
  )
);

// ============================VideoPreview=================================================
const VideoPreview = React.memo(
  ({
    isLive,
    mediaStreamRef,
    audioStreamRef,
    onRecordingStart,
    timeRemaining,
    cameraError,
    onCameraError,
    onCameraGranted,
    onMicGranted,
    onBothGranted,
    preAnswerCountdown,
  }) => {
    const videoRef = useRef(null);

    useEffect(() => {
      let videoStream = null;
      let audioStream = null;

      const initCamera = async () => {
        try {
          // Step 1 — video + audio together
          videoStream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 24, max: 30 } },
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          onCameraGranted(); // CAMERA_ACCESS_GRANTED

          // Step 2 — dedicated audio stream for recorder
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          onMicGranted(); // MIC_ACCESS_GRANTED

          if (videoRef.current) {
            videoRef.current.srcObject = videoStream;
            mediaStreamRef.current     = videoStream;
            audioStreamRef.current     = audioStream;
          }

          onBothGranted(); // CAMERA_MIC_ACCESS_GRANTED
          onRecordingStart(videoStream, audioStream);
        } catch (err) {
          console.error("Camera init error:", err);
          onCameraError(err.message);
        }
      };

      initCamera();

      return () => {
        if (videoStream) videoStream.getTracks().forEach((t) => t.stop());
        if (audioStream) audioStream.getTracks().forEach((t) => t.stop());
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Paper
        elevation={0}
        sx={{
          position: "relative", height: "100%", minHeight: 400,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          border: "1px solid rgba(255,255,255,0.53)",
          borderRadius: 3, display: "flex", alignItems: "center",
          justifyContent: "center", overflow: "hidden",
        }}
      >
        {isLive && !cameraError && (
          <Chip
            icon={<FiberManualRecord sx={{ fontSize: 12, animation: "pulse 2s infinite" }} />}
            label="Recording"
            size="small"
            sx={{
              position: "absolute", top: 24, left: 24,
              bgcolor: "rgba(0,0,0,0.5)", backdropFilter: "blur(10px)",
              color: "white", zIndex: 10,
              "& .MuiChip-icon": { color: "#ef4444" },
            }}
          />
        )}

        {timeRemaining > 0 && !cameraError && (
          <Box sx={{
            position: "absolute", top: 24, right: 24,
            px: 1.2, py: 0.5, borderRadius: 999,
            bgcolor: timeRemaining <= 30 ? "rgba(127,29,29,0.95)" : "rgba(15,23,42,0.8)",
            border: timeRemaining <= 30 ? "1px solid rgba(248,113,113,0.9)" : "1px solid rgba(148,163,184,0.6)",
            color: "white", fontSize: 15, fontWeight: 600, zIndex: 10,
            display: "flex", alignItems: "center", gap: 0.75,
            boxShadow: timeRemaining <= 30 ? "0 0 0 1px rgba(248,113,113,0.6),0 0 12px rgba(220,38,38,0.8)" : "none",
            animation: timeRemaining <= 30 ? "timerPulse 1.5s ease-in-out infinite" : "none",
          }}>
            <AccessTime sx={{ fontSize: 16, color: timeRemaining <= 30 ? "#fecaca" : "#e5e7eb" }} />
            <Box component="span">
              {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, "0")}
            </Box>
          </Box>
        )}

        {preAnswerCountdown > 0 && !cameraError && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(15, 23, 42, 0.42)",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                minWidth: 240,
                px: 3,
                py: 2,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid rgba(148, 163, 184, 0.6)",
                //bgcolor: "rgba(2, 6, 23, 0.72)",
                bgcolor: "rgba(15,23,42,0.8)",
                backdropFilter: "blur(2px)",
                color: "white",
                opacity: 0.9,
                
              }}
            >
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                You can start recording your answer in seconds:
              </Typography>
              <Typography variant="h2" fontWeight={700} lineHeight={1}>
                {preAnswerCountdown}
              </Typography>
            </Box>
          </Box>
        )}

        {cameraError ? (
          <Stack alignItems="center" spacing={2}>
            <Videocam sx={{ fontSize: 96, color: "rgba(255,255,255,0.2)" }} />
            <Typography variant="h6" color="rgba(255,255,255,0.4)">
              Camera / Microphone access denied
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.3)" textAlign="center" px={2}>
              {cameraError}
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.25)" textAlign="center" px={2}>
              Please enable camera and microphone permissions in your browser settings and refresh the page.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
              sx={{
                color: "rgba(255,255,255,0.6)",
                borderColor: "rgba(255,255,255,0.3)",
                textTransform: "none",
                "&:hover": { borderColor: "rgba(255,255,255,0.6)" },
              }}
            >
              Reload & Try Again
            </Button>
          </Stack>
        ) : (
          <video
            ref={videoRef} autoPlay playsInline muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}

        <style>{`
          @keyframes pulse      { 0%,100%{opacity:1}50%{opacity:0.5} }
          @keyframes timerPulse {
            0%,100%{transform:translateY(0);box-shadow:0 0 0 0 rgba(248,113,113,0.6)}
            50%    {transform:translateY(-1px);box-shadow:0 0 0 6px rgba(248,113,113,0)}
          }
        `}</style>
      </Paper>
    );
  }
);

// ============================Module-level fetch guard=================================================
let _hasFetched = false;

// ============================Main Interview Component=================================================
const Interview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const interviewId = useMemo(() => {
    const fromState = location.state?.interviewId;
    if (fromState) {
      persistInterviewId(fromState);
      return fromState;
    }
    return getPersistedId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [interviewData,        setInterviewData]        = useState({});
  const [currentQuestion,      setCurrentQuestion]      = useState(1);
  const [isLive,               setIsLive]               = useState(false);
  const [isTtsCompletedForCurrentQuestion, setIsTtsCompletedForCurrentQuestion] = useState(false);
  const [isErr,                setIsErr]                = useState(null);
  const [audioUrl,             setAudioUrl]             = useState(null);
  const [isPreStartActive,     setIsPreStartActive]     = useState(false);
  const [preStartSeconds,      setPreStartSeconds]      = useState(0);
  const [timeRemaining,        setTimeRemaining]        = useState(0);
  const [isSubmitting,         setIsSubmitting]         = useState(false);
  const [isFinalizing,         setIsFinalizing]         = useState(false);
  const [isFeedbackOpen,       setIsFeedbackOpen]       = useState(false);
  const [feedbackStarted,      setFeedbackStarted]      = useState(false);
  const [feedbackStep,         setFeedbackStep]         = useState(0);
  const [feedbackAnswers,      setFeedbackAnswers]      = useState(() => ({}));
  const [finalUploadError,     setFinalUploadError]     = useState(null);
  const [isSendingLogs,        setIsSendingLogs]        = useState(false);
  const [cameraError,          setCameraError]          = useState(null);
  const [preAnswerCountdown,   setPreAnswerCountdown]   = useState(0);

  // ── Warning popup state ────────────────────────────────────────────────────
  // showReRecordWarning: shown on 2nd attempt (1st re-record after reload)
  // showFinalWarning:    shown on 3rd attempt (2nd re-record) — must acknowledge before auto-advance
  const [showReRecordWarning,  setShowReRecordWarning]  = useState(false);
  const [showFinalWarning,     setShowFinalWarning]     = useState(false);

  const reRecordWarningQNum    = useRef(null); // which question triggered the warning
  // Callback stored so we can call it AFTER the user acknowledges the final warning
  const pendingAutoAdvanceRef  = useRef(null);

  // Refs
  const isResumingRef        = useRef(false);
  const mediaRecorderRef     = useRef(null);
  const audioRecorderRef     = useRef(null);
  const mediaStreamRef       = useRef(null);
  const audioStreamRef       = useRef(null);
  const audioChunksRef       = useRef([]);
  const audioPlayerRef       = useRef(null);
  const timerRef             = useRef(null);
  const preAnswerIntervalRef = useRef(null);
  const hasPlayedFirstTtsRef = useRef(false);
  const interviewDataRef     = useRef({});
  const currentQuestionRef   = useRef(1);
  const finalUploadPromiseRef = useRef(null);
  const finalUploadPayloadRef = useRef(null);
  const completePromiseRef = useRef(null);
  const completionReadyRef = useRef(false);

  const FEEDBACK_QUESTIONS = useMemo(
    () => ([
      {
        id: "overall",
        kind: "stars",
        text: "How would you rate your overall interview experience?",
        scale: [
          { value: 5, label: "Excellent" },
          { value: 4, label: "Good" },
          { value: 3, label: "Average" },
          { value: 2, label: "Poor" },
          { value: 1, label: "Very Poor" },
        ],
      },
      {
        id: "clarity",
        kind: "options",
        text: "How clear and understandable were the interview questions?",
        options: [
          "Very Clear",
          "Mostly Clear",
          "Somewhat Clear",
          "Confusing",
          "Very Confusing",
        ],
      },
      {
        id: "difficulty",
        kind: "options",
        text: "How would you rate the difficulty level of the interview?",
        options: ["Very Easy", "Easy", "Moderate", "Difficult", "Very Difficult"],
      },
      {
        id: "platform",
        kind: "options",
        text: "How was your experience with the interview platform (recording, navigation, etc.)?",
        options: ["Very Smooth", "Smooth", "Neutral", "Some Issues", "Major Issues"],
      },
      {
        id: "instructions",
        kind: "options",
        text: "How clear were the instructions provided before/during the interview?",
        options: ["Very Clear", "Clear", "Average", "Unclear", "Very Unclear"],
      },
      {
        id: "timing",
        kind: "options",
        text: "Was the time given for each question sufficient?",
        options: [
          "More than sufficient",
          "Sufficient",
          "Neutral",
          "Slightly insufficient",
          "Not sufficient at all",
        ],
      },
      {
        id: "technical",
        kind: "options",
        text: "Did you face any technical issues during the interview?",
        options: [
          "No Issues",
          "Minor Issues (did not affect much)",
          "Moderate Issues (some disruption)",
          "Major Issues (significantly affected experience)",
        ],
      },
    ]),
    [],
  );

  const FEEDBACK_RATING_META = useMemo(
    () => ({
      1: { label: "Very Poor", color: "#ef4444", buttonBg: "#ef4444" },
      2: { label: "Poor", color: "#f59e0b", buttonBg: "#f59e0b" },
      3: { label: "Average", color: "#0ea5e9", buttonBg: "#0ea5e9" },
      4: { label: "Good", color: "#4f46e5", buttonBg: "#4f46e5" },
      5: { label: "Excellent", color: "#65a30d", buttonBg: "#65a30d" },
    }),
    [],
  );

  useEffect(() => { interviewDataRef.current = interviewData; }, [interviewData]);
  useEffect(() => { currentQuestionRef.current = currentQuestion; }, [currentQuestion]);

  const addLogWithQuestionText = useCallback(
    (event, question = null, extra = {}) => {
      const qNum =
        typeof question === "number"
          ? question
          : typeof currentQuestionRef.current === "number"
            ? currentQuestionRef.current
            : null;

      const questionText =
        qNum && qNum > 0
          ? interviewDataRef.current?.questions?.[qNum - 1]?.questionText
          : undefined;

      addLog(interviewId, event, qNum, {
        questionText: questionText ?? null,
        ...extra,
      });
    },
    [interviewId],
  );

  const getBlobDurationSeconds = useCallback((blob) => {
    return new Promise((resolve) => {
      if (!blob || blob.size === 0) { resolve(0); return; }
      try {
        const el  = document.createElement("video");
        const url = URL.createObjectURL(blob);
        el.preload = "metadata";
        el.onloadedmetadata = () => {
          const dur = el.duration;
          if (Number.isFinite(dur) && dur > 0) {
            URL.revokeObjectURL(url);
            resolve(Math.floor(dur));
          } else {
            el.currentTime = 1e101;
            el.ontimeupdate = () => {
              el.ontimeupdate = null;
              const d = el.duration;
              URL.revokeObjectURL(url);
              resolve(Number.isFinite(d) && d > 0 ? Math.floor(d) : 0);
            };
          }
        };
        el.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
        el.src = url;
      } catch { resolve(0); }
    });
  }, []);

  const startPreAnswerCountdown = useCallback(() => {
    if (preAnswerIntervalRef.current) {
      clearInterval(preAnswerIntervalRef.current);
      preAnswerIntervalRef.current = null;
    }

    let remaining = 3;
    setIsTtsCompletedForCurrentQuestion(false);
    setPreAnswerCountdown(remaining);

    preAnswerIntervalRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
        setPreAnswerCountdown(0);
        timerRef.current?.start?.();
        setIsTtsCompletedForCurrentQuestion(true);
        return;
      }
      setPreAnswerCountdown(remaining);
    }, 1000);
  }, []);

  const freezeForFinalSubmission = useCallback(() => {
    // Stop any countdown/tts and pause the timer immediately
    timerRef.current?.pause?.();
    setIsLive(false);

    if (preAnswerIntervalRef.current) {
      clearInterval(preAnswerIntervalRef.current);
      preAnswerIntervalRef.current = null;
    }
    setPreAnswerCountdown(0);

    try {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = "";
      }
    } catch (e) {}
  }, []);

  const playAudio = useCallback((audioData) => {
    try {
      let url;
      if (audioData instanceof Blob) {
        url = URL.createObjectURL(audioData);
      } else if (typeof audioData === "string") {
        if (audioData.startsWith("http") || audioData.startsWith("blob:") || audioData.startsWith("data:")) {
          url = audioData;
        } else {
          try {
            const bytes = atob(audioData);
            const arr   = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            url = URL.createObjectURL(new Blob([arr], { type: "audio/mpeg" }));
          } catch { url = audioData; }
        }
      } else { url = audioData; }

      setAudioUrl(url);

      const el = audioPlayerRef.current;
      if (!el) return;
      el.src = url;
      el.load();
      el.onended = () => {
        startPreAnswerCountdown();
      };
      el.play()?.catch(() => {
        startPreAnswerCountdown();
      });
    } catch {
      startPreAnswerCountdown();
    }
  }, [startPreAnswerCountdown]);

  //method to fetch audio script for a question
  const fetchAudioScript = useCallback(async (questionText) => {
    if (!questionText?.trim()) {
      timerRef.current?.start?.();
      setIsTtsCompletedForCurrentQuestion(true);
      return;
    }
    try {
      const response = await AxiosService.post(
        "/api/Interview/text-to-speech",
        { Text: questionText },
        { responseType: "blob" }
      );
      if (response) {
        playAudio(response);
      } else {
        timerRef.current?.start?.();
        setIsTtsCompletedForCurrentQuestion(true);
      }
    } catch (err) {
      console.error("[Interview] TTS error:", err);
      timerRef.current?.start?.();
      setIsTtsCompletedForCurrentQuestion(true);
    }
  }, [playAudio]);

  const uploadFiles = useCallback(async (videoBlob, audioBlob, questionOrder, durationSeconds) => {
    const data       = interviewDataRef.current;
    const totalQ     = data?.questions?.length || 0;
    const questionId = data?.questions?.[questionOrder - 1]?.id;

    if (!questionId) {
      console.error("[Interview] questionId not found for order:", questionOrder);
      return false;
    }

    const formData = new FormData();
    formData.append("videoFile",       new File([videoBlob], `question_${questionId}.webm`,       { type: "video/webm" }));
    formData.append("audioFile",       new File([audioBlob], `question_${questionId}_audio.webm`, { type: "audio/webm" }));
    formData.append("interviewId",     interviewId);
    formData.append("questionId",      questionId);
    formData.append("durationSeconds", durationSeconds?.toString() ?? "0");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/interview/response`,
        { method: "POST", body: formData }
      );
      if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);
      await res.json();

      if (questionOrder < totalQ) {
        saveProgress(interviewId, { currentQuestion: questionOrder + 1 });
      } else {
        clearProgress(interviewId);
      }

      return true;
    } catch (err) {
      console.error("[Interview] Upload error:", err);
      return false;
    }
  }, [interviewId]);

  const startRecordersForQuestion = useCallback((questionNum) => {
    if (!mediaStreamRef.current || !audioStreamRef.current) return;

    const videoRecorder = new RecordRTC(mediaStreamRef.current, {
      type: "video",
      mimeType: "video/webm;codecs=vp8,opus",
      bitsPerSecond: 128000,
    });
    mediaRecorderRef.current = videoRecorder;

    const audioRecorder      = new MediaRecorder(audioStreamRef.current, { mimeType: "audio/webm;codecs=opus" });
    audioRecorderRef.current = audioRecorder;
    audioChunksRef.current   = [];

    audioRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
    audioRecorder.onstop          = () => console.log(`[Interview] Audio recorder stopped Q${questionNum}`);

    videoRecorder.startRecording();
    audioRecorder.start();
    setIsLive(true);
  }, []);

  // ── Permission granted callbacks ──────────────────────────────────────────
  const handleCameraGranted = useCallback(() => {
    addLogWithQuestionText("CAMERA_ACCESS_GRANTED", null, { device: "camera" });
    console.log("[Interview] CAMERA_ACCESS_GRANTED");
  }, [addLogWithQuestionText]);

  const handleMicGranted = useCallback(() => {
    addLogWithQuestionText("MIC_ACCESS_GRANTED", null, { device: "microphone" });
    console.log("[Interview] MIC_ACCESS_GRANTED");
  }, [addLogWithQuestionText]);

  const handleBothGranted = useCallback(() => {
    addLogWithQuestionText("CAMERA_MIC_ACCESS_GRANTED", null, { device: "camera+microphone" });
    console.log("[Interview] CAMERA_MIC_ACCESS_GRANTED");
  }, [addLogWithQuestionText]);

  const handleRecordingStart = useCallback((videoStream, audioStream) => {
    mediaStreamRef.current = videoStream;
    audioStreamRef.current = audioStream;
    startRecordersForQuestion(1);
  }, [startRecordersForQuestion]);

  const handleCameraError = useCallback((errMessage) => {
    setCameraError(errMessage);
    // Timer API exposes pause/resume; stop does not exist.
    timerRef.current?.pause?.();
    if (preAnswerIntervalRef.current) {
      clearInterval(preAnswerIntervalRef.current);
      preAnswerIntervalRef.current = null;
    }
    setPreAnswerCountdown(0);
    setIsTtsCompletedForCurrentQuestion(false);
    addLogWithQuestionText("CAMERA_MIC_ACCESS_DENIED", null, { error: errMessage });
  }, [addLogWithQuestionText]);

  //TODO: Remove this function - add logs to database 
  const insertLogs = useCallback(async (logs) => {
    if (isSendingLogs) return;
    setIsSendingLogs(true);
    try {
      console.log("[Interview] Sending logs:", logs);
      await AxiosService.post(`/api/EventLogs/bulk`, logs);
      console.log("[Interview] Logs sent successfully");
    } catch (err) {
      console.error("[Interview] Failed to send logs:", err);
    } finally {
      setIsSendingLogs(false);
    }
  }, [interviewId, isSendingLogs]);

  const completeInterviewApiOnly = useCallback(async () => {
    if (completionReadyRef.current) return;

    await AxiosService.post(`/api/Interview/${interviewId}/complete`);

    const finalLogs = getLogs(interviewId);
    const totalTabSwitchCount = finalLogs.filter(
      (l) => l?.event === "TAB_SWITCHED_AWAY",
    ).length;

    addLogWithQuestionText("INTERVIEW_COMPLETED", null, { totalLogs: finalLogs.length });
    addLogWithQuestionText("SUMMARY", null, {
      totalTabSwitchCount,
      attemptSummary: loadAttempts(interviewId),
    });

    clearProgress(interviewId);
    clearSession(interviewId);
    clearAttempts(interviewId);
    clearPersistedId();

    completionReadyRef.current = true;
  }, [addLogWithQuestionText, interviewId]);

  const completeInterviewAndRedirect = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // If completion already ran in background, don't call again.
      if (!completionReadyRef.current) {
        await completeInterviewApiOnly();
      }
      navigate(`/interview-completion/${interviewId}`);
    } catch (err) {
      console.error("[Interview] /complete error:", err?.response?.data || err?.message);
      setIsErr("Failed to submit interview. Please contact your recruiter.");
      setIsSubmitting(false);
    }
  }, [completeInterviewApiOnly, interviewId, isSubmitting, navigate]);

  // ── Shared advance logic ───────────────────────────────────────────────────
  const advanceToNextQuestion = useCallback(async (fromQuestion) => {
    const data   = interviewDataRef.current;
    const totalQ = data?.questions?.length || 0;

    if (fromQuestion >= totalQ) {
      completeInterviewAndRedirect();
      return;
    }

    const nextQ = fromQuestion + 1;
    saveProgress(interviewId, { currentQuestion: nextQ });
    setCurrentQuestion(nextQ);
    setIsTtsCompletedForCurrentQuestion(false);
    setPreAnswerCountdown(0);
    audioChunksRef.current = [];

    const nextText = data?.questions?.[nextQ - 1]?.questionText;
    if (nextText) {
      await fetchAudioScript(nextText);
    } else {
      timerRef.current?.start?.();
      setIsTtsCompletedForCurrentQuestion(true);
    }

    startRecordersForQuestion(nextQ);
  }, [interviewId, completeInterviewAndRedirect, fetchAudioScript, startRecordersForQuestion]);

  // ── Auto-advance with upload (used when max attempts exceeded) ──────────────
  // Stops both recorders cleanly using event-based sequencing (not fixed timeouts),
  // collects whatever was recorded (even if empty), uploads it, then advances.
  const autoAdvanceWithUpload = useCallback(async (qNum) => {
    const data   = interviewDataRef.current;
    const totalQ = data?.questions?.length || 0;

    let videoBlob = new Blob([], { type: "video/webm" });
    let audioBlob = new Blob([], { type: "audio/webm" });

    // ── Step 1: Stop audio recorder and wait for onstop (event-based, not timeout) ──
    // MediaRecorder fires ondataavailable BEFORE onstop, so all chunks are
    // guaranteed to be in audioChunksRef.current by the time onstop resolves.
    try {
      const audioRec = audioRecorderRef.current;
      if (audioRec && audioRec.state === "recording") {
        await new Promise((resolve) => {
          const originalOnStop = audioRec.onstop;
          audioRec.onstop = (e) => {
            if (originalOnStop) originalOnStop(e);
            resolve();
          };
          audioRec.stop();
        });
      }
    } catch (e) {  }

    // ── Step 2: Collect audio chunks (all guaranteed present after onstop) ──
    if (audioChunksRef.current.length > 0) {
      audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    }

    // ── Step 3: Stop video recorder (RecordRTC uses callback, already event-based) ──
    try {
      if (mediaRecorderRef.current) {
        videoBlob = await new Promise((resolve) => {
          mediaRecorderRef.current.stopRecording(() => {
            const blob = mediaRecorderRef.current.getBlob();
            resolve(blob && blob.size > 0 ? blob : new Blob([], { type: "video/webm" }));
          });
        });
      }
    } catch (e) { }

    const durationSeconds = await getBlobDurationSeconds(videoBlob);

    if (qNum >= totalQ) {
      // For last question, start upload+complete in background and show feedback modal.
      freezeForFinalSubmission();
      setFinalUploadError(null);
      finalUploadPayloadRef.current = {
        videoBlob,
        audioBlob,
        questionNumber: qNum,
        durationSeconds,
      };

      const uploadPromise = uploadFiles(videoBlob, audioBlob, qNum, durationSeconds);
      finalUploadPromiseRef.current = uploadPromise;
      uploadPromise.catch((err) => {
        console.error("[Interview] Final answer background upload failed (auto-advance):", err);
        setFinalUploadError(err?.message || "Upload failed. Please try again.");
      });

      // Trigger /complete in background once upload succeeds (same as normal submit).
      completePromiseRef.current = uploadPromise
        .then(() => completeInterviewApiOnly())
        .catch((err) => {
          console.error("[Interview] Background completion chain failed (auto-advance):", err);
          completionReadyRef.current = false;
          throw err;
        });

      setFeedbackStep(0);
      setFeedbackAnswers({});
      setFeedbackStarted(false);
      setIsFeedbackOpen(true);
      return;
    }

    // Upload in background — do not block advancing
    uploadFiles(videoBlob, audioBlob, qNum, durationSeconds).catch((err) =>
      console.error("[Interview] Background upload (auto-advance) failed:", err)
    );

    advanceToNextQuestion(qNum);
  }, [
    advanceToNextQuestion,
    completeInterviewApiOnly,
    getBlobDurationSeconds,
    uploadFiles,
    freezeForFinalSubmission,
  ]);

  // ── Handler: user acknowledges the final warning → auto-advance ───────────
  const handleFinalWarningAcknowledge = useCallback(() => {
    setShowFinalWarning(false);
    const fn = pendingAutoAdvanceRef.current;
    pendingAutoAdvanceRef.current = null;
    if (fn) fn();
  }, []);

  // ── Core fetch logic ──────────────────────────────────────────────────────
  const runFetchInterviewData = useCallback(async () => {
    if (!interviewId) {
      setIsErr("Interview session not found. Please go back and start again.");
      return;
    }

    try {
      const response = await AxiosService.post(`/api/Interview/${interviewId}/start`);
      setInterviewData(response);
      interviewDataRef.current = response;

      const totalQ = response?.questions?.length || 0;
      const saved  = loadProgress(interviewId);
      const savedQ = saved?.currentQuestion ?? 1;

      const navEntries = performance.getEntriesByType("navigation");
      const isReload   = navEntries?.[0]?.type === "reload";

      if (saved && savedQ > 1 && savedQ <= totalQ) {
        const existingReloads = getLogs(interviewId).filter((l) => l.event === "PAGE_RELOADED").length;
        const reloadCount     = existingReloads + 1;
        addLogWithQuestionText("PAGE_RELOADED", savedQ, { count: reloadCount, totalQuestions: totalQ });
        console.log("[Interview] Logs so far:", getLogs(interviewId));
        isResumingRef.current = true;
        setCurrentQuestion(savedQ);
        setIsPreStartActive(false);
        return;
      }

      if (isReload && savedQ <= 1) {
        const existingReloads = getLogs(interviewId).filter((l) => l.event === "PAGE_RELOADED").length;
        const reloadCount     = existingReloads + 1;
        addLogWithQuestionText("PAGE_RELOADED", 1, { count: reloadCount, totalQuestions: totalQ, note: "reload_on_q1" });
        console.log("[Interview] Logs so far:", getLogs(interviewId));
        isResumingRef.current = true;
        setCurrentQuestion(1);
        setIsPreStartActive(false);
        return;
      }

      if (saved && savedQ > totalQ) {
        clearAllForInterview(interviewId);
        clearAttempts(interviewId);
        addLogWithQuestionText("FRESH_START", 1, { reason: "stale_progress_cleared", totalQ });
        setIsPreStartActive(true);
        setPreStartSeconds(5);
        return;
      }

      clearLogs(interviewId);
      clearAttempts(interviewId);
      const existingSession = loadSession(interviewId);
      const attemptCount    = (existingSession?.attemptCount ?? 0) + 1;
      saveSession(interviewId, { startedAt: new Date().toISOString(), attemptCount });
      addLogWithQuestionText("FRESH_START", 1, { totalQ, attemptCount });
      setCurrentQuestion(1);
      setIsPreStartActive(true);
      setPreStartSeconds(5);

    } catch (err) {
      console.error("[Interview] fetchInterviewData error:", err);
      setIsErr(err?.message || "Failed to load interview. Please try again.");
    }
  }, [interviewId, addLogWithQuestionText]);

  const fetchInterviewData = useCallback(async () => {
    if (_hasFetched) return;
    _hasFetched = true;
    await runFetchInterviewData();
  }, [runFetchInterviewData]);

  const handleNextQuestion = useCallback(async () => {
    if (cameraError) {
      return;
    }
    if (isFinalizing || isSubmitting || isFeedbackOpen) {
      return;
    }
    if (!mediaRecorderRef.current) {
      
      return;
    }
    if (!audioRecorderRef.current || audioRecorderRef.current.state !== "recording") {
      
      return;
    }

    const data   = interviewDataRef.current;
    const totalQ = data?.questions?.length || 0;

    audioRecorderRef.current.stop();

    const videoBlob = await new Promise((resolve) => {
      mediaRecorderRef.current.stopRecording(() => resolve(mediaRecorderRef.current.getBlob()));
    });

    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });

    await new Promise((r) => setTimeout(r, 300));
    const durationSeconds = await getBlobDurationSeconds(videoBlob);

    if (currentQuestion >= totalQ) {
      freezeForFinalSubmission();
      // Start the last-answer upload in the background and engage the candidate
      // with feedback questions instead of blocking on a loader.
      setFinalUploadError(null);
      finalUploadPayloadRef.current = {
        videoBlob,
        audioBlob,
        questionNumber: currentQuestion,
        durationSeconds,
      };
      const uploadPromise = uploadFiles(
        videoBlob,
        audioBlob,
        currentQuestion,
        durationSeconds,
      );
      finalUploadPromiseRef.current = uploadPromise;
      uploadPromise.catch((err) => {
        console.error("[Interview] Final answer background upload failed:", err);
        setFinalUploadError(err?.message || "Upload failed. Please try again.");
      });

      // When the upload succeeds, immediately trigger /complete in background.
      // Redirect is still gated by the 5th feedback answer submit.
      completePromiseRef.current = uploadPromise
        .then(() => completeInterviewApiOnly())
        .catch((err) => {
          // Upload failure is handled above; ensure completion isn't marked ready.
          console.error("[Interview] Background completion chain failed:", err);
          completionReadyRef.current = false;
          throw err;
        });

      setFeedbackStep(0);
      setFeedbackAnswers({});
      setFeedbackStarted(false);
      setIsFeedbackOpen(true);
      return;
    }

    uploadFiles(videoBlob, audioBlob, currentQuestion, durationSeconds).catch((err) =>
      console.error("[Interview] Background upload failed:", err)
    );

    await advanceToNextQuestion(currentQuestion);
  }, [
    currentQuestion,
    getBlobDurationSeconds,
    uploadFiles,
    advanceToNextQuestion,
    cameraError,
    isFinalizing,
    isSubmitting,
    isFeedbackOpen,
  ]);

  const retryFinalUpload = useCallback(() => {
    const payload = finalUploadPayloadRef.current;
    if (!payload) return;
    setFinalUploadError(null);
    const uploadPromise = uploadFiles(
      payload.videoBlob,
      payload.audioBlob,
      payload.questionNumber,
      payload.durationSeconds,
    );
    finalUploadPromiseRef.current = uploadPromise;
    uploadPromise.catch((err) => {
      console.error("[Interview] Final answer background upload failed (retry):", err);
      setFinalUploadError(err?.message || "Upload failed. Please try again.");
    });

    // Rebuild completion chain after retry as well.
    completePromiseRef.current = uploadPromise
      .then(() => completeInterviewApiOnly())
      .catch((err) => {
        console.error("[Interview] Background completion chain failed (retry):", err);
        completionReadyRef.current = false;
        throw err;
      });
  }, [completeInterviewApiOnly, uploadFiles]);

  const handleFeedbackSubmit = useCallback(async () => {
    // Do NOT call /complete here. /complete is triggered when the last interview response upload succeeds.
    // Feedback submission should not block redirect even on failure.
    setIsFeedbackOpen(false);
    navigate(`/interview-completion/${interviewId}`);
  }, [interviewId, navigate]);

  const submitFeedbackAnswer = useCallback(
    async ({ interviewId: iid, question, answer }) => {
      // New feedback endpoint (frontend only). Backend should accept:
      // { interviewId, question, answer }
      await AxiosService.post(`/api/Interview/feedback`, {
        interview_id: iid,
        question,
        feedback : answer,
      });
    },
    [],
  );

  const handleTimeUp = useCallback(() => {
    if (cameraError) return;
    handleNextQuestion();
  }, [cameraError, handleNextQuestion]);

  const currentQuestionText = useMemo(
    () => interviewData?.questions?.[currentQuestion - 1]?.questionText || "",
    [currentQuestion, interviewData]
  );

  const currentQuestionDuration = useMemo(() => {
    const s = interviewData?.questions?.[currentQuestion - 1]?.expectedDurationSeconds;
    return typeof s === "number" && s > 0 ? s : 0;
  }, [currentQuestion, interviewData]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Reset fetch guard on unmount so back-navigation works
  useEffect(() => {
    return () => { _hasFetched = false; };
  }, []);

  useEffect(() => {
    fetchInterviewData();
  }, [fetchInterviewData]);

  // bfcache safety net
  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return;
      _hasFetched = false;
      setInterviewData({});
      setCurrentQuestion(1);
      setIsLive(false);
      setIsTtsCompletedForCurrentQuestion(false);
      setIsErr(null);
      setIsPreStartActive(false);
      setCameraError(null);
      hasPlayedFirstTtsRef.current = false;
      isResumingRef.current        = false;
      _hasFetched = true;
      runFetchInterviewData();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [runFetchInterviewData]);

  // ── Per-question attempt tracking ─────────────────────────────────────────
  // Fires every time currentQuestion changes while interview is active.
  //
  // Attempt 1 → normal, log QUESTION_RECORDING_STARTED
  // Attempt 2 → show first warning popup (last chance), log QUESTION_RE_RECORD_ATTEMPT
  // Attempt 3 → show final alert, wait for acknowledge, THEN auto-advance with upload
  // Attempt 4+ → auto-advance silently (safety net)
  useEffect(() => {
    if (isPreStartActive)                    return;
    if (!interviewData?.questions?.length)   return;
    if (isErr)                               return;

    const qNum     = currentQuestion;
    const totalQ   = interviewData.questions.length;
    const newCount = incrementAttempt(interviewId, qNum);

    if (newCount === 1) {
      // ── First attempt — normal ──────────────────────────────────────────
      addLogWithQuestionText("QUESTION_RECORDING_STARTED", qNum, {
        attemptNumber: 1,
      });

    } else if (newCount === WARN_AT_ATTEMPT) {
      // ── Second attempt — show first warning popup ───────────────────────
      // Candidate still gets to record; we warn them the next reload auto-advances.
      addLogWithQuestionText("QUESTION_RE_RECORD_ATTEMPT", qNum, {
        attemptNumber: newCount,
        maxAllowed: MAX_ATTEMPTS_PER_QUESTION,
        action: "first_warning_shown",
      });
      reRecordWarningQNum.current = qNum;
      setShowReRecordWarning(true);

    } else if (newCount === MAX_ATTEMPTS_PER_QUESTION) {
      // ── Third attempt — show final alert, THEN auto-advance ────────────
      // We do NOT advance until the user clicks "OK" on the final dialog.
      addLogWithQuestionText("QUESTION_MAX_ATTEMPTS_EXCEEDED", qNum, {
        attemptNumber: newCount,
        maxAllowed: MAX_ATTEMPTS_PER_QUESTION,
        action: "final_warning_shown_then_auto_advance",
      });

      reRecordWarningQNum.current = qNum;

      // Store the advance action; it fires only after acknowledgement
      pendingAutoAdvanceRef.current = () => {
        autoAdvanceWithUpload(qNum);
      };

      setShowFinalWarning(true);

    } else if (newCount > MAX_ATTEMPTS_PER_QUESTION) {
      // ── Beyond max — silent safety net auto-advance ────────────────────
      addLogWithQuestionText("QUESTION_MAX_ATTEMPTS_EXCEEDED", qNum, {
        attemptNumber: newCount,
        maxAllowed: MAX_ATTEMPTS_PER_QUESTION,
        action: "auto_advanced_silently",
      });

      try { if (audioRecorderRef.current?.state === "recording") audioRecorderRef.current.stop(); } catch (e) {}
      try { if (mediaRecorderRef.current) mediaRecorderRef.current.stopRecording?.(); } catch (e) {}

      if (qNum >= totalQ) {
        autoAdvanceWithUpload(qNum);
      } else {
        advanceToNextQuestion(qNum);
      }

    } else {
      // ── Any other re-record attempt between warn and max ───────────────
      addLogWithQuestionText("QUESTION_RE_RECORD_ATTEMPT", qNum, {
        attemptNumber: newCount,
        maxAllowed: MAX_ATTEMPTS_PER_QUESTION,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, isPreStartActive, interviewData?.questions?.length]);

  useEffect(() => {
    if (!isPreStartActive || isErr || preStartSeconds <= 0) {
      if (isPreStartActive && preStartSeconds <= 0) setIsPreStartActive(false);
      return;
    }
    const id = setInterval(() => {
      setPreStartSeconds((p) => {
        if (p - 1 <= 0) { clearInterval(id); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isPreStartActive, preStartSeconds, isErr]);

  useEffect(() => {
    if (isErr || isPreStartActive) return;
    if (!interviewData?.questions?.length) return;

    if (isResumingRef.current) {
      isResumingRef.current = false;

      // TTS plays only on the very first attempt (attempt 1).
      // On any reload (attempt 2+) the candidate has already heard the question —
      // skip TTS, start the timer immediately, and enable the Next button.
      // incrementAttempt() already ran in the attempt-tracking effect above,
      // so count 1 = first ever load, count 2+ = reload/re-record.
      const existingAttempts = loadAttempts(interviewId);
      const attemptCount     = existingAttempts[currentQuestion] ?? 0;

      if (attemptCount >= 2) {
        // Reload scenario — candidate already heard TTS, skip it
        timerRef.current?.start?.();
        setIsTtsCompletedForCurrentQuestion(true);
      } else {
        const text = interviewData.questions[currentQuestion - 1]?.questionText;
        if (text) {
          fetchAudioScript(text);
        } else {
          timerRef.current?.start?.();
          setIsTtsCompletedForCurrentQuestion(true);
        }
      }
      return;
    }

    if (currentQuestion !== 1 || hasPlayedFirstTtsRef.current) return;
    hasPlayedFirstTtsRef.current = true;
    const text = interviewData.questions[0]?.questionText;
    if (text) {
      fetchAudioScript(text);
    } else {
      setIsTtsCompletedForCurrentQuestion(true);
    }
  }, [isPreStartActive, isErr, interviewData, currentQuestion, fetchAudioScript]);

  useEffect(() => {
    return () => { if (audioUrl) URL.revokeObjectURL(audioUrl); };
  }, [audioUrl]);

  useEffect(() => {
    return () => {
      if (preAnswerIntervalRef.current) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let hiddenTime = null;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenTime = Date.now();
      } else {
        if (hiddenTime && Date.now() - hiddenTime > 1000) {
          const existingTabSwitches = getLogs(interviewId).filter((l) => l.event === "TAB_SWITCHED_AWAY").length;
          const tabSwitchCount      = existingTabSwitches + 1;
          const existingTabReturns  = getLogs(interviewId).filter((l) => l.event === "TAB_RETURNED").length;
          const tabReturnCount      = existingTabReturns + 1;
          addLogWithQuestionText("TAB_SWITCHED_AWAY", currentQuestion, { count: tabSwitchCount });
          addLogWithQuestionText("TAB_RETURNED",      currentQuestion, { count: tabReturnCount });
        }
        hiddenTime = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { document.removeEventListener("visibilitychange", onVisibility); };
  }, [interviewId, currentQuestion, addLogWithQuestionText]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Your interview is in progress. Are you sure you want to leave?";
    };
    const onKeyDown = (e) => {
      if (e.key === "F5" || (e.ctrlKey && e.key === "r") || (e.metaKey && e.key === "r")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  // ── Permission revocation — separate handlers per device ──────────────────
  useEffect(() => {
    let cameraStatus = null;
    let micStatus    = null;

    const stopAll = () => {
      timerRef.current?.pause?.();
      setIsLive(false);
      if (preAnswerIntervalRef.current) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
      }
      setPreAnswerCountdown(0);
      setIsTtsCompletedForCurrentQuestion(false);
      try {
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
          audioPlayerRef.current.src = "";
        }
      } catch (e) {}
      try {
        if (audioRecorderRef.current?.state === "recording") audioRecorderRef.current.stop();
      } catch (e) {}
      try {
        if (mediaRecorderRef.current) mediaRecorderRef.current.stopRecording?.();
      } catch (e) {}
    };

    const handleCameraRevoked = () => {
      if (cameraError) return;
      setCameraError("Camera access was revoked.");
      stopAll();
      addLogWithQuestionText("CAMERA_REVOKED_MID_INTERVIEW", null, {
        device: "camera",
        question: currentQuestionRef.current,
      });
    };

    const handleMicRevoked = () => {
      if (cameraError) return;
      setCameraError("Microphone access was revoked.");
      stopAll();
      addLogWithQuestionText("MIC_REVOKED_MID_INTERVIEW", null, {
        device: "microphone",
        question: currentQuestionRef.current,
      });
    };

    const setup = async () => {
      try {
        cameraStatus = await navigator.permissions.query({ name: "camera" });
        micStatus    = await navigator.permissions.query({ name: "microphone" });

        cameraStatus.onchange = () => {
          if (cameraStatus.state === "denied" || cameraStatus.state === "prompt") handleCameraRevoked();
        };
        micStatus.onchange = () => {
          if (micStatus.state === "denied" || micStatus.state === "prompt") handleMicRevoked();
        };
      } catch (e) {
        console.warn("[Interview] Permissions API not supported:", e);
      }
    };

    setup();

    return () => {
      if (cameraStatus) cameraStatus.onchange = null;
      if (micStatus)    micStatus.onchange    = null;
    };
  }, [cameraError, addLogWithQuestionText]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <audio ref={audioPlayerRef} autoPlay />

      <Backdrop
        open={isFinalizing}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 10,
          //bgcolor: "rgba(248, 250, 252, 0.92)", // app background with glass overlay
          backdropFilter: "blur(6px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            //bgcolor: "#ffffff",
            border: "1px solid rgba(148, 163, 184, 0.35)",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
            maxWidth: 520,
            width: "calc(100% - 48px)",
          }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: 4,
                display: "grid",
                placeItems: "center",
                bgcolor: "transparent",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 88,
                  height: 88,
                  display: "grid",
                  placeItems: "center",
                  "--g": "#16a34a",
                  "--gSoft": "rgba(22, 163, 74, 0.26)",
                  "--g2": "rgba(22, 163, 74, 0.10)",
                  // segmented outer ring
                  background:
                    "conic-gradient(from 90deg, var(--g) 0 12deg, transparent 12deg 30deg, var(--g) 30deg 42deg, transparent 42deg 60deg, var(--g) 60deg 72deg, transparent 72deg 90deg, var(--g) 90deg 102deg, transparent 102deg 120deg, var(--g) 120deg 132deg, transparent 132deg 150deg, var(--g) 150deg 162deg, transparent 162deg 180deg, var(--g) 180deg 192deg, transparent 192deg 210deg, var(--g) 210deg 222deg, transparent 222deg 240deg, var(--g) 240deg 252deg, transparent 252deg 270deg, var(--g) 270deg 282deg, transparent 282deg 300deg, var(--g) 300deg 312deg, transparent 312deg 330deg, var(--g) 330deg 342deg, transparent 342deg 360deg)",
                  borderRadius: "999px",
                  boxShadow: "0 0 0 6px var(--g2) inset",
                  animation: "segSpin 1.6s linear infinite",
                  "@keyframes segSpin": {
                    to: { transform: "rotate(360deg)" },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 10,
                    borderRadius: "999px",
                    background: "#ffffff",
                    boxShadow: "0 0 0 1px rgba(22, 163, 74, 0.22) inset",
                  },
                }}
              >
                {/* Equalizer bars */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "inline-flex",
                    alignItems: "flex-end",
                    gap: 0.6,
                    height: 26,
                    "& i": {
                      display: "block",
                      width: 5,
                      borderRadius: 2,
                      background: "var(--g)",
                      boxShadow: "0 0 0 3px var(--gSoft)",
                      animation: "barBounce 1s ease-in-out infinite",
                    },
                    "& i:nth-of-type(1)": { height: 16, animationDelay: "0s" },
                    "& i:nth-of-type(2)": { height: 22, animationDelay: "0.1s" },
                    "& i:nth-of-type(3)": { height: 18, animationDelay: "0.2s" },
                    "& i:nth-of-type(4)": { height: 24, animationDelay: "0.3s" },
                    "& i:nth-of-type(5)": { height: 16, animationDelay: "0.4s" },
                    "@keyframes barBounce": {
                      "0%, 100%": { transform: "scaleY(0.75)", opacity: 0.75 },
                      "50%": { transform: "scaleY(1.2)", opacity: 1 },
                    },
                  }}
                >
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a" }}>
                Your interview is being submitted
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.7)", mt: 0.75 }}>
                Please wait for sometime. Don&apos;t close or refresh this page.
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: -0.5 }}>
              <Typography variant="caption" sx={{ color: "rgba(15, 23, 42, 0.6)", fontWeight: 600 }}>
                Almost done
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  pl: 0.4,
                  "& span": {
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    bgcolor: "primary.main",
                    opacity: 0.7,
                    animation: "dotBounce 1.1s ease-in-out infinite",
                  },
                  "& span:nth-of-type(2)": { animationDelay: "0.15s" },
                  "& span:nth-of-type(3)": { animationDelay: "0.3s" },
                  "@keyframes dotBounce": {
                    "0%, 80%, 100%": { transform: "translateY(0)", opacity: 0.55 },
                    "40%": { transform: "translateY(-5px)", opacity: 1 },
                  },
                }}
              >
                <span />
                <span />
                <span />
              </Box>
            </Stack>
          </Stack>
        </Paper>
      </Backdrop>

      <InterviewFeedbackModal
        open={isFeedbackOpen}
        interviewId={interviewId}
        started={feedbackStarted}
        setStarted={setFeedbackStarted}
        step={feedbackStep}
        setStep={setFeedbackStep}
        questions={FEEDBACK_QUESTIONS}
        answers={feedbackAnswers}
        setAnswers={setFeedbackAnswers}
        apiError={finalUploadError}
        onRetryUpload={retryFinalUpload}
        onSubmitAnswer={submitFeedbackAnswer}
        onFinish={handleFeedbackSubmit}
      />

      {/* ── First warning popup: shown on 2nd attempt ───────────────────── */}
      {/* Candidate still records; warns them that next reload will auto-advance */}
      <Dialog
        open={showReRecordWarning}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <WarningAmberRounded sx={{ color: "#f59e0b", fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700}>Re-recording Detected</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            This is your <strong>last attempt</strong> to record Question {reRecordWarningQNum.current}.
            If you reload again, your response will be automatically saved and you will be moved to the next question.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setShowReRecordWarning(false)}
            sx={{ textTransform: "none", borderRadius: 1.5 }}
          >
            I Understand, Continue Recording
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Final warning popup: shown on 3rd attempt ───────────────────── */}
      {/* Auto-advance ONLY fires after the candidate clicks OK */}
      <Dialog
        open={showFinalWarning}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <WarningAmberRounded sx={{ color: "#ef4444", fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700}>Maximum Attempts Reached</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            You have reached the maximum number of recording attempts for{" "}
            <strong>Question {reRecordWarningQNum.current}</strong>.
            Your current response will be saved and you will be automatically moved to the next question.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={handleFinalWarningAcknowledge}
            sx={{ textTransform: "none", borderRadius: 1.5 }}
          >
            OK, Move to Next Question
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 4 }}>
        <Container maxWidth="xl">

          {isErr ? (
            <Box sx={{ height: "70vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", px: 2 }}>
              <Alert severity="error" sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h6" color="error.main" sx={{ m: 0 }}>{isErr}</Typography>
              </Alert>
            </Box>

          ) : isPreStartActive ? (
            <Box sx={{ height: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 2 }}>
              <Typography variant="h4" fontWeight={600}>
                Your interview will start in {preStartSeconds} seconds
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please get ready. Ensure your camera and microphone are enabled.
              </Typography>
            </Box>

          ) : interviewData?.questions ? (
            <Grid container spacing={3}>

              <Grid item size={{ xs: 12, md: 8 }}>
                <VideoPreview
                  isLive={isLive}
                  mediaStreamRef={mediaStreamRef}
                  audioStreamRef={audioStreamRef}
                  onRecordingStart={handleRecordingStart}
                  timeRemaining={timeRemaining}
                  cameraError={cameraError}
                  onCameraError={handleCameraError}
                  onCameraGranted={handleCameraGranted}
                  onMicGranted={handleMicGranted}
                  onBothGranted={handleBothGranted}
                  preAnswerCountdown={preAnswerCountdown}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
                <Stack spacing={3} sx={{ flex: 1, alignItems: "stretch", justifyContent: "space-between" }}>

                  {cameraError && (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="warning"
                          size="small"
                          variant="contained"
                          onClick={() => window.location.reload()}
                          sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          Reload
                        </Button>
                      }
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.3 }}>
                        Camera or microphone blocked
                      </Typography>
                      <Typography variant="body2">
                        Allow access in your browser's address bar, then reload to continue.
                      </Typography>
                    </Alert>
                  )}

                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <Timer
                      ref={timerRef}
                      durationSeconds={currentQuestionDuration}
                      questionKey={currentQuestion}
                      onTimeUp={handleTimeUp}
                      startPaused={true}
                      onTick={setTimeRemaining}
                    />
                    <CurrentQuestion
                      question={currentQuestionText}
                      questionNumber={currentQuestion}
                      onNext={handleNextQuestion}
                      isSubmit={currentQuestion === interviewData?.questions?.length}
                      isNextEnabled={isTtsCompletedForCurrentQuestion && !cameraError}
                      isLoading={isFinalizing || isFeedbackOpen}
                    />
                  </Box>

                  <Paper elevation={0} sx={{ p: 1.5, border: "1px solid rgba(170,168,160,0.3)", borderRadius: 2 }}>
                    <Typography variant="body2" color="#ef6e13">
                      <strong style={{ fontWeight: 600, fontSize: 16 }}>AI Monitoring Active :</strong>{" "}
                      Your behavior, responses, and engagement are being monitored. Please maintain
                      eye contact with the camera and avoid switching tabs or typing.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={2}>
                      Take your time to provide a thoughtful answer. Speak clearly and look at the
                      camera. Please do not switch tabs or refresh the page while recording.
                    </Typography>
                  </Paper>
                </Stack>
              </Grid>

              <Grid item size={{ xs: 12, md: 8 }}>
                <ProgressTracker
                  currentQuestion={currentQuestion}
                  totalQuestions={interviewData?.questions?.length}
                />
              </Grid>

            </Grid>

          ) : (
            <Box sx={{ height: "70vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", px: 2 }}>
              <Typography variant="h5">Loading...</Typography>
            </Box>
          )}

        </Container>
      </Box>
    </>
  );
};

export default Interview;